import { cloneElement, isValidElement, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { OverlayPortal } from '../overlays/OverlayFoundation'
import { resolveComponentVariant } from '../componentContract'
import styles from './Primitives.module.css'

const placements = ['top', 'bottom', 'left', 'right']
const GAP = 8
const MARGIN = 8

// The bubble is portaled and positioned in viewport coordinates: list rows live
// inside scroll containers whose computed overflow-x clips an absolutely
// positioned sibling, which truncated reorder tooltips to a stub.
function computePosition(anchor, size, placement) {
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  let resolved = placement
  if (placement === 'top' && anchor.top - size.height - GAP < MARGIN) resolved = 'bottom'
  else if (placement === 'bottom' && anchor.bottom + size.height + GAP > viewportHeight - MARGIN) resolved = 'top'
  else if (placement === 'left' && anchor.left - size.width - GAP < MARGIN) resolved = 'right'
  else if (placement === 'right' && anchor.right + size.width + GAP > viewportWidth - MARGIN) resolved = 'left'

  let top
  let left
  if (resolved === 'top') {
    top = anchor.top - size.height - GAP
    left = anchor.left + (anchor.width - size.width) / 2
  } else if (resolved === 'bottom') {
    top = anchor.bottom + GAP
    left = anchor.left + (anchor.width - size.width) / 2
  } else if (resolved === 'left') {
    top = anchor.top + (anchor.height - size.height) / 2
    left = anchor.left - size.width - GAP
  } else {
    top = anchor.top + (anchor.height - size.height) / 2
    left = anchor.right + GAP
  }

  return {
    top: Math.min(Math.max(MARGIN, top), Math.max(MARGIN, viewportHeight - size.height - MARGIN)),
    left: Math.min(Math.max(MARGIN, left), Math.max(MARGIN, viewportWidth - size.width - MARGIN)),
  }
}

export function Tooltip({ content, placement = 'top', children }) {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState(null)
  const wrapRef = useRef(null)
  const surfaceRef = useRef(null)
  const id = useId()
  const resolvedPlacement = resolveComponentVariant('Tooltip placement', placement, placements, 'top')

  const reposition = useCallback(() => {
    const wrap = wrapRef.current
    const surface = surfaceRef.current
    if (!wrap || !surface) return
    setPosition(computePosition(wrap.getBoundingClientRect(), surface.getBoundingClientRect(), resolvedPlacement))
  }, [resolvedPlacement])

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null)
      return
    }
    reposition()
  }, [content, open, reposition])

  useEffect(() => {
    if (!open) return undefined
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [open, reposition])

  if (!isValidElement(children)) return children

  const trigger = cloneElement(children, {
    'aria-describedby': open ? id : children.props['aria-describedby'],
    // The bubble is the hover affordance; a native title on top of it would
    // duplicate the accessible name in a second, unstyled browser tooltip.
    title: '',
    onMouseEnter: (event) => {
      children.props.onMouseEnter?.(event)
      setOpen(true)
    },
    onMouseLeave: (event) => {
      children.props.onMouseLeave?.(event)
      setOpen(false)
    },
    onFocus: (event) => {
      children.props.onFocus?.(event)
      setOpen(true)
    },
    onBlur: (event) => {
      children.props.onBlur?.(event)
      setOpen(false)
    },
    onKeyDown: (event) => {
      children.props.onKeyDown?.(event)
      if (event.key === 'Escape') setOpen(false)
    },
  })

  return (
    <span ref={wrapRef} className={styles.tooltipWrap}>
      {trigger}
      {open ? (
        <OverlayPortal>
          <span
            ref={surfaceRef}
            id={id}
            role="tooltip"
            className={styles.tooltip}
            style={position ? { top: `${position.top}px`, left: `${position.left}px` } : { top: 0, left: 0, opacity: 0 }}
          >
            {content}
          </span>
        </OverlayPortal>
      ) : null}
    </span>
  )
}
